import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiCamera, FiTrash2, FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import './AddProduct.css';

const AddProduct = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showCatModal, setShowCatModal] = useState(false);
  const [creatingCat, setCreatingCat] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    mrp: '',
    image: '',
    stock: '',
    unit: 'piece',
    brand: '',
    tags: '',
    isFeatured: false
  });

  const [newCat, setNewCat] = useState({
    name: '',
    icon: '📁',
    sortOrder: 1
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/products/categories/all');
      if (data.categories) setCategories(data.categories);
    } catch (err) {
      console.warn('Failed to load categories:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG and WebP images are allowed');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('files', file);

      const { data } = await api.post('/upload?folder=product', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });

      const uploaded = data.files?.[0];
      if (!uploaded) throw new Error('Upload failed');

      setForm(prev => ({ ...prev, image: uploaded.viewLink }));
      toast.success('Product image uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCat.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setCreatingCat(true);
    try {
      const { data } = await api.post('/admin/categories', {
        name: newCat.name.trim(),
        icon: newCat.icon.trim(),
        sortOrder: Number(newCat.sortOrder || 1)
      });

      if (data.success && data.category) {
        toast.success('Category created successfully!');
        setCategories(prev => [...prev, data.category]);
        setForm(prev => ({ ...prev, category: data.category._id }));
        setNewCat({ name: '', icon: '📁', sortOrder: 1 });
        setShowCatModal(false);
      } else {
        toast.error('Failed to create category');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create category');
    } finally {
      setCreatingCat(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.stock) {
      toast.error('Name, price and stock are required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        mrp: form.mrp ? Number(form.mrp) : undefined,
        stock: Number(form.stock),
        tags: form.tags ? form.tags.split(',').map(t => t.trim()) : []
      };
      await api.post('/admin/products', payload);
      toast.success('Product added successfully!');
      navigate('/admin/products');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-top-bar">
        <button className="back-btn" onClick={() => navigate('/admin/products')}>
          <FiArrowLeft />
        </button>
        <h1>Add Product</h1>
      </div>

      <form className="add-product-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Product Name *</label>
          <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="e.g. Cello Butterflow Pen" required />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea name="description" value={form.description} onChange={handleChange} placeholder="Product description..." rows={3} />
        </div>

        <div className="form-group">
          <label>Category</label>
          <div className="category-select-wrapper">
            <select name="category" value={form.category} onChange={handleChange}>
              <option value="">Select category</option>
              {categories.map(cat => (
                <option key={cat._id} value={cat._id}>{cat.name}</option>
              ))}
            </select>
            <button
              type="button"
              className="add-cat-btn"
              onClick={() => setShowCatModal(true)}
            >
              <FiPlus /> New
            </button>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Price (₹) *</label>
            <input type="number" name="price" value={form.price} onChange={handleChange} placeholder="0" required />
          </div>
          <div className="form-group">
            <label>MRP (₹)</label>
            <input type="number" name="mrp" value={form.mrp} onChange={handleChange} placeholder="0" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Stock *</label>
            <input type="number" name="stock" value={form.stock} onChange={handleChange} placeholder="0" required />
          </div>
          <div className="form-group">
            <label>Unit</label>
            <select name="unit" value={form.unit} onChange={handleChange}>
              <option value="piece">Piece</option>
              <option value="pack">Pack</option>
              <option value="box">Box</option>
              <option value="ream">Ream</option>
              <option value="set">Set</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Product Image</label>
          <div className="image-upload-container">
            {form.image ? (
              <div className="image-preview-box">
                <img src={form.image} alt="Product Preview" className="product-image-thumbnail" />
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={() => setForm(prev => ({ ...prev, image: '' }))}
                >
                  <FiTrash2 /> Remove
                </button>
              </div>
            ) : (
              <label className="image-upload-placeholder">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={uploadingImage}
                />
                {uploadingImage ? (
                  <div className="loader-small">Uploading to Cloudinary...</div>
                ) : (
                  <>
                    <FiCamera className="upload-icon" />
                    <span>Upload Product Image</span>
                  </>
                )}
              </label>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>Brand</label>
          <input type="text" name="brand" value={form.brand} onChange={handleChange} placeholder="e.g. Cello" />
        </div>

        <div className="form-group">
          <label>Tags (comma separated)</label>
          <input type="text" name="tags" value={form.tags} onChange={handleChange} placeholder="notebook, writing, school" />
        </div>

        <label className="toggle-label">
          <input type="checkbox" name="isFeatured" checked={form.isFeatured} onChange={handleChange} />
          Featured Product
        </label>

        <button type="submit" className="submit-btn" disabled={loading || uploadingImage}>
          {loading ? 'Adding...' : 'Add Product'}
        </button>
      </form>

      {/* Inline Create Category Modal */}
      {showCatModal && (
        <div className="cat-modal-overlay">
          <div className="cat-modal-content">
            <h3>Create New Category</h3>
            <form onSubmit={handleCreateCategory}>
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  value={newCat.name}
                  onChange={(e) => setNewCat(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Fine Art Supplies"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Emoji/Icon</label>
                  <input
                    type="text"
                    value={newCat.icon}
                    onChange={(e) => setNewCat(prev => ({ ...prev, icon: e.target.value }))}
                    placeholder="e.g. 🎨"
                  />
                </div>
                <div className="form-group">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    value={newCat.sortOrder}
                    onChange={(e) => setNewCat(prev => ({ ...prev, sortOrder: e.target.value }))}
                    min="1"
                    placeholder="1"
                  />
                </div>
              </div>
              <div className="cat-modal-actions">
                <button
                  type="button"
                  className="cat-cancel-btn"
                  onClick={() => setShowCatModal(false)}
                  disabled={creatingCat}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="cat-submit-btn"
                  disabled={creatingCat}
                >
                  {creatingCat ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProduct;
